/**
 * Inline Edit Component.
 *
 * Displays a value as static text. Double-clicking activates edit mode,
 * showing an input field. Changes are committed on blur or Enter, and
 * cancelled on Escape. Supports text, number, date, and textarea types.
 */

import { useState, useRef, useEffect, useCallback } from "react";

type InputType = "text" | "number" | "date" | "textarea";

interface InlineEditProps {
  /** The current display value (shown when not editing). */
  value: string;
  /** Called when the user commits a new value. */
  onSave: (value: string) => void;
  /** Input type — determines the control rendered in edit mode. */
  type?: InputType;
  /** Placeholder shown in the input when empty. */
  placeholder?: string;
  /** Whether editing is allowed (default: true). */
  editable?: boolean;
  /** Custom display formatter — transforms the raw value for display. */
  displayValue?: string;
  /** Additional class names for the static display wrapper. */
  className?: string;
  /** Additional class names for the input element. */
  inputClassName?: string;
  /** Number input: step attribute. */
  step?: string;
  /** Number input: min attribute. */
  min?: string;
  /** Textarea: rows. */
  rows?: number;
}

function InlineEdit({
  value,
  onSave,
  type = "text",
  placeholder,
  editable = true,
  displayValue,
  className = "",
  inputClassName = "",
  step,
  min,
  rows = 3,
}: InlineEditProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  // Sync draft when value changes externally while not editing.
  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  // Focus and select input when entering edit mode.
  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      if (type !== "date" && "select" in inputRef.current) {
        inputRef.current.select();
      }
    }
  }, [editing, type]);

  const commit = useCallback(() => {
    setEditing(false);
    if (draft !== value) {
      onSave(draft);
    }
  }, [draft, value, onSave]);

  const cancel = useCallback(() => {
    setEditing(false);
    setDraft(value);
  }, [value]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && type !== "textarea") {
        e.preventDefault();
        commit();
      } else if (e.key === "Escape") {
        e.preventDefault();
        cancel();
      }
    },
    [commit, cancel, type],
  );

  if (!editable) {
    return (
      <span className={className}>
        {displayValue || value || (
          <span className="text-[var(--text-tertiary)]">
            {placeholder || "—"}
          </span>
        )}
      </span>
    );
  }

  if (!editing) {
    return (
      <span
        onDoubleClick={() => setEditing(true)}
        className={`cursor-pointer rounded-[var(--radius-sm)] px-1 -mx-1 transition-colors hover:bg-[var(--surface-sunken)] ${className}`}
        title="Double-click to edit"
      >
        {displayValue || value || (
          <span className="text-[var(--text-tertiary)] italic">
            {placeholder || "Click to edit"}
          </span>
        )}
      </span>
    );
  }

  const baseInputClass = `rounded-[var(--radius-sm)] border border-[var(--color-interactive-primary)] bg-[var(--surface-base)] px-2 text-[var(--text-primary)] focus:outline-none focus-visible:shadow-[var(--focus-ring)] ${inputClassName}`;

  if (type === "textarea") {
    return (
      <textarea
        ref={inputRef as React.RefObject<HTMLTextAreaElement>}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={handleKeyDown}
        rows={rows}
        placeholder={placeholder}
        className={`w-full py-1 text-sm ${baseInputClass}`}
      />
    );
  }

  return (
    <input
      ref={inputRef as React.RefObject<HTMLInputElement>}
      type={type}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={handleKeyDown}
      placeholder={placeholder}
      step={type === "number" ? step : undefined}
      min={type === "number" ? min : undefined}
      className={`h-7 py-0 text-sm ${baseInputClass}`}
    />
  );
}

export default InlineEdit;
